// SEO Utility Functions for BookaTherapy

/**
 * Generate Organization Schema (JSON-LD)
 */
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "BookaTherapy",
    legalName: "BookaTherapy Inc.",
    url: "https://bookatherapy.com",
    logo: "https://bookatherapy.com/logo.png",
    description:
      "Canada's leading platform for booking professional massage therapy. Connect with certified RMTs for home, mobile, or clinic massage services.",
    foundingDate: "2024",
    address: {
      "@type": "PostalAddress",
      addressCountry: "CA",
      addressRegion: "Ontario",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-234-567-890",
      contactType: "Customer Service",
      email: "info@bookatherapy.com",
      availableLanguage: ["English", "French"],
    },
    sameAs: [
      "https://facebook.com/bookatherapy",
      "https://instagram.com/bookatherapy",
      "https://twitter.com/bookatherapy",
      "https://linkedin.com/company/bookatherapy",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      ratingCount: "10000",
      bestRating: "5",
      worstRating: "1",
    },
  };
}

/**
 * Generate Local Business Schema for a clinic
 */
export function generateClinicSchema(clinic) {
  return {
    "@context": "https://schema.org",
    "@type": "HealthAndBeautyBusiness",
    "@id": `https://bookatherapy.com/clinics/${clinic.slug}`,
    name: clinic.name,
    description:
      clinic.description ||
      `Professional massage therapy services at ${clinic.name}. Book registered massage therapists (RMT) in ${clinic.city}.`,
    image: clinic.image || "https://bookatherapy.com/default-clinic.jpg",
    telephone: clinic.phone,
    email: clinic.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: clinic.address,
      addressLocality: clinic.city,
      addressRegion: clinic.province || "ON",
      postalCode: clinic.postal_code,
      addressCountry: "CA",
    },
    geo: clinic.coordinates
      ? {
          "@type": "GeoCoordinates",
          latitude: clinic.coordinates.lat,
          longitude: clinic.coordinates.lng,
        }
      : undefined,
    openingHoursSpecification: clinic.hours
      ? generateOpeningHours(clinic.hours)
      : undefined,
    priceRange: "$$",
    paymentAccepted: ["Cash", "Credit Card", "Insurance Direct Billing"],
    hasMap: `https://maps.google.com/?q=${encodeURIComponent(clinic.address)}`,
    aggregateRating: clinic.rating
      ? {
          "@type": "AggregateRating",
          ratingValue: clinic.rating.average,
          ratingCount: clinic.rating.count,
          bestRating: "5",
          worstRating: "1",
        }
      : undefined,
  };
}

/**
 * Generate Service Schema
 */
export function generateServiceSchema(service) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.name,
    description: service.description,
    provider: {
      "@type": "Organization",
      name: "BookaTherapy",
    },
    areaServed: {
      "@type": "Country",
      name: "Canada",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: `https://bookatherapy.com/book?service=${service.id}`,
    },
    offers: {
      "@type": "Offer",
      price: "100.00",
      priceCurrency: "CAD",
      availability: "https://schema.org/InStock",
    },
  };
}

/**
 * Generate BreadcrumbList Schema
 */
export function generateBreadcrumbSchema(breadcrumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generate FAQ Schema
 */
export function generateFAQSchema(faqs) {
  // Return null if no FAQ data is provided
  if (!faqs || !Array.isArray(faqs) || faqs.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

/**
 * Generate Article Schema (for blog posts)
 */
export function generateArticleSchema(article) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.image,
    author: {
      "@type": "Person",
      name: article.author || "BookaTherapy Team",
    },
    publisher: {
      "@type": "Organization",
      name: "BookaTherapy",
      logo: {
        "@type": "ImageObject",
        url: "https://bookatherapy.com/logo.png",
      },
    },
    datePublished: article.publishedDate,
    dateModified: article.modifiedDate || article.publishedDate,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://bookatherapy.com/blog/${article.slug}`,
    },
  };
}

/**
 * Generate BlogPosting Schema (for blog posts with more detailed structure)
 */
export function generateBlogPostingSchema({
  headline,
  description,
  image,
  url,
  datePublished,
  dateModified,
  articleBody,
  keywords,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    headline: headline,
    description: description,
    image:
      image ||
      "https://bookatherapy.com/static/media/logo2.fbfb04de82c528272816.png",
    author: {
      "@type": "Person",
      name: "BookaTherapy",
    },
    publisher: {
      "@type": "Organization",
      name: "BookaTherapy",
      logo: {
        "@type": "ImageObject",
        url: "https://bookatherapy.com/static/media/logo2.fbfb04de82c528272816.png",
      },
    },
    datePublished: datePublished,
    dateModified: dateModified || datePublished,
    articleBody: articleBody,
    keywords: keywords,
  };
}

/**
 * Generate Website Schema
 */
export function generateWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "BookaTherapy",
    url: "https://bookatherapy.com",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://bookatherapy.com/search?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * Helper: Generate opening hours specification
 */
function generateOpeningHours(hours) {
  if (!hours || typeof hours !== "object") return undefined;

  const daysMap = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };

  return Object.entries(hours)
    .map(([day, time]) => {
      if (!time || time === "Closed") return null;

      return {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: daysMap[day.toLowerCase()],
        opens: time.open,
        closes: time.close,
      };
    })
    .filter(Boolean);
}

/**
 * SEO Meta Tags Generator
 */
export function generateMetaTags({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  noindex = false,
  nofollow = false,
}) {
  const fullTitle = title
    ? `${title} | BookaTherapy`
    : "BookaTherapy - Book Professional Massage Therapy in Canada";
  const finalDescription =
    description ||
    "Find and book professional massage therapy with certified RMTs. Home, mobile, or clinic appointments across Canada. Transparent pricing, personalized matching.";

  return {
    title: fullTitle,
    description: finalDescription,
    keywords:
      keywords ||
      "massage therapy, RMT, registered massage therapist, massage booking, mobile massage, home massage, clinic massage, Canada",
    canonical: canonical || "https://bookatherapy.com",
    ogTitle: ogTitle || fullTitle,
    ogDescription: ogDescription || finalDescription,
    ogImage: ogImage || "https://bookatherapy.com/og-image.jpg",
    ogType,
    twitterCard,
    robots:
      (noindex ? "noindex" : "index") +
      "," +
      (nofollow ? "nofollow" : "follow"),
  };
}

/**
 * Generate City/Location Page Meta Tags
 */
export function generateCityPageMeta(city, province = "ON") {
  const title = `Massage Therapy in ${city}, ${province}`;
  const description = `Book professional massage therapy in ${city}, ${province}. Find certified RMTs offering home, mobile, and clinic massage services. Same-day availability, transparent pricing.`;
  const keywords = `massage therapy ${city}, RMT ${city}, massage ${city}, registered massage therapist ${city}, ${city} massage, mobile massage ${city}`;

  return generateMetaTags({
    title,
    description,
    keywords,
    canonical: `https://bookatherapy.com/locations/${city
      .toLowerCase()
      .replace(/\s+/g, "-")}`,
    ogTitle: title,
    ogDescription: description,
  });
}

/**
 * Generate Service Page Meta Tags
 */
export function generateServicePageMeta(serviceName) {
  const title = `${serviceName} Therapy`;
  const description = `Book professional ${serviceName.toLowerCase()} with certified RMTs across Canada. Personalized matching, transparent pricing, and same-day availability.`;
  const keywords = `${serviceName.toLowerCase()}, ${serviceName.toLowerCase()} therapy, RMT ${serviceName.toLowerCase()}, book ${serviceName.toLowerCase()}`;

  return generateMetaTags({
    title,
    description,
    keywords,
    canonical: `https://bookatherapy.com/services/${serviceName
      .toLowerCase()
      .replace(/\s+/g, "-")}`,
    ogTitle: title,
    ogDescription: description,
  });
}
